import { useFormik } from 'formik';
import { useShallow } from 'zustand/react/shallow';
import Stack from '@mui/material/Stack';
import type { TimeSchedulerValue } from '@symphona/ui-kit';
import { makeInstallationStepSchema } from '@components/Checkout/steps/InstallationStep/utils/installationStepSchema';
import { buildInitialValues } from '@components/Checkout/steps/InstallationStep/utils/installationStepUtils';
import { InfoDialog } from '@components/Checkout/steps/InstallationStep/InfoDialog';
import type { ActiveFormProps } from '@components/Checkout/steps/InstallationStep/InstallationStep.types';
import { MethodSelector } from '@components/Checkout/steps/InstallationStep/MethodSelector';
import { SelfInstallSection } from '@components/Checkout/steps/InstallationStep/SelfInstallSection';
import { TechnicianSection } from '@components/Checkout/steps/InstallationStep/TechnicianSection';
import { useInitialInfoDialog } from '@components/Checkout/steps/InstallationStep/useInitialInfoDialog';
import { useShoppingCart } from '@hooks/useShoppingCart/useShoppingCart';
import { useAnalytics } from '@hooks/useAnalytics/useAnalytics';
import { selectQualifiedAddressId, useQualificationFlowStore } from '@store/qualificationStore/qualificationStore';
import LoadingButton from '@shared/components/buttons/LoadingButton/LoadingButton';
import type { InstallationFormValues, InstallationMethod } from '@t/checkout.types';

export const ActiveForm = ({
  sessionId,
  technicianOptions,
  pickupAddress,
  workTime,
  isSlotsLoading,
  savedValues,
  isSaving,
  restrictionDescription,
  showInitialInfoDialog,
  onInfoDialogShown,
  onSave,
  onDateRangeChange,
}: ActiveFormProps) => {
  const { cart } = useShoppingCart(sessionId ?? '');
  const { resolvedAddressId, qualifiedAddressId } = useQualificationFlowStore(
    useShallow((s) => ({
      resolvedAddressId: s.resolvedAddressId,
      qualifiedAddressId: selectQualifiedAddressId(s),
    }))
  );
  const analytics = useAnalytics();

  const formik = useFormik<InstallationFormValues>({
    initialValues: buildInitialValues(savedValues, technicianOptions),
    validationSchema: makeInstallationStepSchema(technicianOptions),
    enableReinitialize: true,
    onSubmit: (values) => {
      if (sessionId && cart) {
        analytics.fire('trackInstallationOptionSaved', {
          sessionId,
          cart,
          qualification: { qualifiedAddressId, resolvedAddressId },
          installation: values,
        });
      }
      onSave(values);
    },
  });

  const { values, setValues, handleSubmit } = formik;

  const hasRestriction = restrictionDescription.length > 0;
  const infoDialog = useInitialInfoDialog(showInitialInfoDialog, true, hasRestriction, onInfoDialogShown);

  const schedulerValue: TimeSchedulerValue = {
    date: values.selectedDate,
    timeSlot: values.selectedTimeSlot
      ? {
          startTime: new Date(values.selectedTimeSlot.startTime),
          endTime: new Date(values.selectedTimeSlot.endTime),
        }
      : null,
    mode: 'WORK_TIME',
  };

  const handleMethodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMethod = e.target.value as InstallationMethod;
    const isSelf = newMethod === 'SELF';
    setValues({
      ...values,
      installationMethod: newMethod,
      hardwareDelivery: isSelf ? 'PICKUP' : undefined,
      deliveryInstructions: '',
      selectedSlotId: null,
      selectedDate: null,
      selectedTimeSlot: null,
    });
  };

  const handleSchedulerChange = (val: TimeSchedulerValue) => {
    const startIso = val.timeSlot?.startTime?.toISOString() ?? '';
    const endIso = val.timeSlot?.endTime?.toISOString() ?? '';

    setValues({
      ...values,
      selectedDate: val.date,
      selectedTimeSlot: val.timeSlot ? { startTime: startIso, endTime: endIso } : values.selectedTimeSlot,
      selectedSlotId: val.timeSlot ? startIso || null : values.selectedSlotId,
    });
  };

  return (
    <Stack gap="30px">
      <InfoDialog open={infoDialog.open} description={restrictionDescription} onClose={infoDialog.close} />

      <MethodSelector
        technicianOptions={technicianOptions}
        value={values.installationMethod}
        onChange={handleMethodChange}
      />

      {values.installationMethod === 'SELF' && (
        <SelfInstallSection
          formik={formik}
          technicianOptions={technicianOptions}
          pickupAddress={pickupAddress}
          restrictionDescription={restrictionDescription}
        />
      )}

      {values.installationMethod === 'TECHNICIAN' && (
        <TechnicianSection
          formik={formik}
          schedulerValue={schedulerValue}
          workTime={workTime}
          isSlotsLoading={isSlotsLoading}
          restrictionDescription={restrictionDescription}
          onSchedulerChange={handleSchedulerChange}
          onDateRangeChange={onDateRangeChange}
        />
      )}

      <Stack alignSelf="flex-start">
        <LoadingButton variant="contained" onClick={() => handleSubmit()} loading={isSaving}>
          Save and Continue
        </LoadingButton>
      </Stack>
    </Stack>
  );
};
